<template>
    <div class="amo-card p-6 rounded-3 earnings-calculator">
        <div class="earnings-display font-title">
            <div>You could earn</div>
            <div class="earnings-amount">£{{ formattedEarnings }}/ year</div>
        </div>
        <div class="text-center font-title">
            <span>equivalent to</span>
            <span class="earnings-amount-monthly">
                £{{ formattedMonthlyEarnings }}/ month
            </span>
        </div>

        <!-- Chart Section -->
        <div class="chart-container">
            <canvas id="earningsChart" class="earnings-chart"></canvas>
        </div>

        <div class="sliders-container">
            <!-- People signing up each month slider -->
            <div class="slider-group">
                <label class="slider-label">
                    <span class="fw-medium font-title text-xl">
                        {{ monthlySignups }}</span
                    >
                    monthly signups
                </label>
                <div class="slider-wrapper">
                    <input
                        type="range"
                        v-model="monthlySignups"
                        class="slider"
                        min="2"
                        max="10000"
                        @input="calculateEarnings"
                    />
                </div>

                <!-- Preset buttons -->
                <div class="preset-buttons">
                    <button
                        v-for="value in presetValues"
                        :key="value"
                        type="button"
                        class="amo-btn amo-btn--secondary preset-btn"
                        :class="{ active: monthlySignups == value }"
                        @click="setPresetValue(value)"
                    >
                        {{ formatCompact(value) }}
                    </button>
                </div>

                <span class="text-xs">
                    The number of people who sign up each month using your
                    referral code
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from "chart.js/auto";

let earningsChart;

export default {
    name: "EarningsCalculator",
    data() {
        return {
            monthlySignups: 100,
            churnRate: 0,
            annualEarnings: 0,
            monthlyEarningsData: [],
            presetValues: [50, 100, 200, 500, 1000, 5000, 10000],
        };
    },
    computed: {
        formattedEarnings() {
            return this.annualEarnings.toLocaleString("en-GB");
        },
        formattedMonthlyEarnings() {
            return Math.round(this.annualEarnings / 12).toLocaleString("en-GB");
        },
        chartLabels() {
            const months = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
            ];
            const currentMonth = new Date().getMonth();
            const nextMonth = (currentMonth + 1) % 12;

            const labels = [];
            for (let i = 0; i < 12; i++) {
                labels.push(months[(nextMonth + i) % 12]);
            }
            return labels;
        },
    },
    methods: {
        formatCompact(value) {
            if (value >= 1000) {
                const wholeThousands = value % 1000 === 0;
                const num = value / 1000;
                const rounded = wholeThousands
                    ? String(num)
                    : String(Number(num.toFixed(1))).replace(/\.0$/, "");
                return `${rounded}k`;
            }
            return String(value);
        },
        calculateEarnings() {
            // Convert churn rate to decimal
            const churnRateDecimal = this.churnRate / 100;

            // Calculate user distribution per month
            const proUsers = this.monthlySignups * 0.7;
            const premiumUsers = this.monthlySignups * 0.2;

            // Monthly earnings per new signup
            const monthlyEarningsPerSignup = proUsers * 1 + premiumUsers * 2.5;

            // Calculate cumulative earnings over 12 months
            let totalAnnualEarnings = 0;
            let cumulativeMonthlyEarnings = 0;
            let cumulativeTotal = 0;
            this.monthlyEarningsData = [];

            for (let month = 1; month <= 12; month++) {
                // Add new signups for this month
                cumulativeMonthlyEarnings += monthlyEarningsPerSignup;

                // Apply churn to existing earnings (users dropping off)
                cumulativeMonthlyEarnings *= 1 - churnRateDecimal;

                // Add this month's earnings to cumulative total
                cumulativeTotal += cumulativeMonthlyEarnings;

                // Store cumulative earnings for chart
                this.monthlyEarningsData.push(Math.round(cumulativeTotal));

                // Add this month's earnings to annual total
                totalAnnualEarnings += cumulativeMonthlyEarnings;
            }

            this.annualEarnings = Math.round(totalAnnualEarnings);
            this.updateChart();
        },

        updateChart() {
            if (earningsChart) {
                earningsChart.data.datasets[0].data = this.monthlyEarningsData;
                earningsChart.update("none"); // No animation for smooth updates
            }
        },

        setPresetValue(value) {
            this.monthlySignups = value;
            this.calculateEarnings();
        },

        initChart() {
            const ctx = document.getElementById("earningsChart");

            if (earningsChart) {
                earningsChart.destroy();
            }

            earningsChart = new Chart(ctx, {
                type: "line",
                data: {
                    labels: this.chartLabels,
                    datasets: [
                        {
                            label: "Cumulative Earnings (£)",
                            data: this.monthlyEarningsData,
                            borderColor: "#322a1c",
                            backgroundColor: "#e5dbd1",
                            borderWidth: 3,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 0, // Remove circles
                            pointHoverRadius: 0, // Remove hover circles
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: "index",
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            backgroundColor: "#322a1c",
                            titleColor: "#f2ede8",
                            bodyColor: "#f2ede8",
                            borderColor: "#322a1c",
                            borderWidth: 1,
                            cornerRadius: 8,
                            displayColors: false,
                            callbacks: {
                                title: function (context) {
                                    return context[0].label;
                                },
                                label: function (context) {
                                    return (
                                        "£" +
                                        context.parsed.y.toLocaleString(
                                            "en-GB"
                                        ) +
                                        " total earned"
                                    );
                                },
                            },
                        },
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            border: {
                                display: false,
                            },
                            grid: {
                                color: "rgba(133, 140, 151, 0.1)", // Much lighter horizontal lines
                                drawBorder: false,
                            },
                            ticks: {
                                color: "var(--text-muted)",
                                font: {
                                    family: "var(--font-body)",
                                    size: 12,
                                },
                                callback: function (value) {
                                    return "£" + value.toLocaleString("en-GB");
                                },
                            },
                        },
                        x: {
                            border: {
                                display: false,
                            },
                            grid: {
                                display: false,
                            },
                            ticks: {
                                color: "var(--text-muted)",
                                font: {
                                    family: "var(--font-body)",
                                    size: 12,
                                },
                            },
                        },
                    },
                },
            });
        },
    },
    mounted() {
        this.calculateEarnings();
        this.$nextTick(() => {
            this.initChart();
        });
    },
    beforeUnmount() {
        if (earningsChart) {
            earningsChart.destroy();
            earningsChart = null;
        }
    },
};
</script>

<style scoped>
/* Mobile-first styles (default) */
.earnings-calculator {
    max-width: 100%;
    overflow: hidden;
}

.card {
    background: var(--background-primary);
    border-radius: 8px;
    padding: 4rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--border-color);
}

.earnings-display {
    text-align: center;
}

.earnings-amount {
    font-size: var(--font-size-3xl);
    font-weight: 600;
    color: var(--text-primary);
    word-break: break-word;
}

.earnings-amount-monthly {
    font-size: var(--font-size-xl);
    font-weight: 500;
    color: var(--text-primary);
    word-break: break-word;
}

.sliders-container {
    display: flex;
    flex-direction: column;
    gap: 4rem;
    margin-top: 2rem;
}

.slider-group {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.slider-label {
    font-size: var(--font-size-base);
    font-weight: 500;
    color: var(--text-primary);
    font-family: var(--font-body);
}

.slider-wrapper {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.slider {
    flex: 1;
    min-width: 0;
    height: 6px;
    border-radius: 3px;
    background: var(--border-color);
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
}

.slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--primary);
    cursor: pointer;
    border: 2px solid var(--white);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--primary);
    cursor: pointer;
    border: 2px solid var(--white);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider::-webkit-slider-track {
    height: 6px;
    border-radius: 3px;
    background: var(--border-color);
}

.slider::-moz-range-track {
    height: 6px;
    border-radius: 3px;
    background: var(--border-color);
    border: none;
}

/* Tablet and desktop styles */
@media (min-width: 769px) {
    .card {
        padding: 3rem;
        max-width: 480px;
    }

    .earnings-amount {
        font-size: var(--font-size-4xl);
    }

    .sliders-container {
        gap: 2rem;
    }

    .slider-wrapper {
        gap: 3rem;
    }

    .earnings-chart {
        height: 250px !important;
    }
}

.chart-container {
    margin: 0;
    max-width: 100%;
    overflow: hidden;
}

.chart-title {
    text-align: center;
    font-size: var(--font-size-lg);
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 3rem;
}

.earnings-chart {
    height: 200px !important;
    width: 100% !important;
}

.preset-buttons {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    margin-top: 1rem;
    flex-wrap: wrap;
}

.preset-btn {
    background: var(--background-primary);
    border: 1px solid var(--border-color);
    color: var(--text-primary);
    font-family: var(--font-body);
    font-size: var(--font-size-sm);
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 48px;
}

.preset-btn:hover {
    background: var(--background-secondary);
    border-color: var(--slate-300);
}

.preset-btn.active {
    background: var(--primary);
    border-color: var(--primary);
    color: var(--button-primary-text);
    font-weight: 600;
}

.preset-btn:focus {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
}
</style>
